import { TemplateType } from 'constants/templateTypes';
import { selectLocaleImprovementsEnabled } from 'core/userContext';
import { useLocaleSettingsContext } from 'modules/campaigns/lib/contexts/LocaleSettingsContext';
import { Locale } from 'modules/domains/template';
import { DefaultLocaleSelectFormField } from 'modules/templateEditor/common/DefaultLocaleSelectFormField';
import { validateWhatsAppTemplateName } from 'modules/templateIndex/utils';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { WhatsAppMedium } from 'constants/messageMediums';
import { BUTTON_SIZES, PrimaryButton } from 'ui/button';
import { BusinessInitiatedTemplate, UserInitiatedTemplate } from 'ui/illustrations';
import { MODAL_SIZES, MODAL_VARIANTS, Modal, ModalBody, ModalFooter, ModalHeader } from 'ui/modal';
import { RadioGroup } from 'ui/radioGroup/radioGroup';
import { Orientation } from 'ui/radioGroup/types';
import { H4, spacing, subtitle2 } from 'ui/theme';
import { getNewDate } from 'utils/date';

import { TemplateNameInput } from '../../components';
import { TEMPLATE_MENU_OPTIONS } from '../../constants';
import { createTemplate } from '../../ducks/actions';
import { selectIsTemplateAsyncProcessing } from '../../ducks/selectors';

type NewWhatsAppTemplateModalProps = {
  /* Function to Close Modal */
  closeModal: () => void;
  /* content strings to render */
  content: {
    templateNameInput: { label: string };
    createTemplateButtonText: string;
    modalHeader: {
      prefix: string;
      suffix: string;
    };
  };
  currentFolderId: number | null;
  /* Identifier of the template type selected */
  id: string;
};

const WhatsAppTemplateOptionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  svg {
    position: relative;
    left: 7px;
  }
`;

const StyledH4 = styled(H4)`
  margin-top: 0;
`;

export const TEMPLATE_TYPES = [
  {
    description:
      'Send pre-approved marketing and transactional messages to start conversations with your customers.',
    id: TemplateType.BusinessInitiated,
    label: (
      <WhatsAppTemplateOptionWrapper>
        <BusinessInitiatedTemplate height={150} width={129} />
        <StyledH4>Business Initiated</StyledH4>
      </WhatsAppTemplateOptionWrapper>
    ),
    value: TemplateType.BusinessInitiated,
  },
  {
    description:
      'Respond with free-form service messages within 24 hours of your customers messaging you, without the need for pre-approval.',
    id: TemplateType.UserInitiated,
    label: (
      <WhatsAppTemplateOptionWrapper>
        <UserInitiatedTemplate />
        <StyledH4>User Initiated</StyledH4>
      </WhatsAppTemplateOptionWrapper>
    ),
    value: TemplateType.UserInitiated,
  },
];

const TEMPLATE_NAME_ERROR_MSG = 'Can only contain lowercase letters, numbers, and underscores.';
const TEMPLATE_NAME_HELP_TEXT = 'Use lowercase letters, numbers, and underscores.';

const GroupLabel = styled.span`
  display: block;
  margin-bottom: ${spacing.half};
  margin-top: ${spacing.plusHalf};
  ${subtitle2};
`;

export const NewWhatsAppTemplateModal = ({
  closeModal = () => null,
  content,
  currentFolderId,
  id = '',
  ...props
}: NewWhatsAppTemplateModalProps) => {
  const { isTemplateDefaultLocaleModifiable } = useLocaleSettingsContext();
  const isLocaleImprovementsEnabled = useSelector(selectLocaleImprovementsEnabled);
  const [templateName, setTemplateName] = useState(
    `template_created_on_${getNewDate().format('ddd_MMM_D_YYYY_H_mm_a')}`.toLocaleLowerCase(),
  );
  const [defaultLocale, setDefaultLocale] = useState('');
  const [templateType, setTemplateType] = useState(TemplateType.BusinessInitiated);
  const [templateNameHasError, setTemplateNameHasError] = useState(false);
  const isTemplateAsyncProcessing = useSelector(selectIsTemplateAsyncProcessing);
  const dispatch = useDispatch();
  useEffect(() => document.getElementById(`whats-app-${id}-template-name-input`)?.focus(), [id]);

  const {
    templateNameInput,
    createTemplateButtonText,
    modalHeader: { prefix, suffix },
  } = content;

  const handleDefaultLocale = (locale: Locale) => {
    setDefaultLocale(locale.tag);
  };

  const handleSubmit = () => {
    const isValidName = validateWhatsAppTemplateName(templateName);
    if (isValidName) {
      setTemplateNameHasError(false);
      dispatch(
        createTemplate({
          currentFolderId,
          name: templateName,
          defaultLocale,
          messageMedium: TEMPLATE_MENU_OPTIONS.whatsApp.slug,
          templateType,
        }),
      );
    } else {
      setTemplateNameHasError(true);
    }
  };

  return (
    <Modal
      withPortal
      id={`whats-app-template-modal`}
      isModalOpen={true}
      onClose={closeModal}
      size={MODAL_SIZES.MEDIUM}
      variant={MODAL_VARIANTS.DEFAULT}
      data-test={`whats-app-template-modal`}
      {...props}
    >
      <ModalHeader data-test="whats-app-template-modal-header">{`${prefix} ${TEMPLATE_MENU_OPTIONS.whatsApp.modalLabel} ${suffix}`}</ModalHeader>
      <ModalBody>
        <TemplateNameInput
          content={templateNameInput}
          templateName={templateName}
          // WhatsApp templates don't allow spaces so go ahead and replace them as user inputs the template name
          setTemplateName={(name) => setTemplateName(name.replaceAll(' ', '_'))}
          id={`whats-app-template-name-input`}
          defaultDate={getNewDate().format('llll')}
          hasError={templateNameHasError}
          errorText={TEMPLATE_NAME_ERROR_MSG}
          helpText={TEMPLATE_NAME_HELP_TEXT}
        />
        {isLocaleImprovementsEnabled && isTemplateDefaultLocaleModifiable && (
          <DefaultLocaleSelectFormField
            onSelect={handleDefaultLocale}
            isProjectLevelDefaultLocale
            messageMedium={WhatsAppMedium.WhatsApp}
          />
        )}
        <GroupLabel>Template Type</GroupLabel>
        <RadioGroup
          value={templateType}
          name="whatsAppOptions"
          options={TEMPLATE_TYPES}
          orientation={Orientation.HORIZONTAL}
          alignRight
          isOverlapped
          onSelect={setTemplateType}
          isBordered
        />
      </ModalBody>
      <ModalFooter>
        <PrimaryButton
          isLoading={isTemplateAsyncProcessing}
          data-test="whats-app-create-template-button"
          onClick={handleSubmit}
          isDisabled={templateName.length === 0 || templateName.length > 255}
          size={BUTTON_SIZES.LARGE}
        >
          <span data-test="whats-app-create-template-button-text">{createTemplateButtonText}</span>
        </PrimaryButton>
      </ModalFooter>
    </Modal>
  );
};
